import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useState } from "react";
import CustomSelect from "../../shared/CustomSelect";
import CustomArea from "../../shared/CustomArea";
import CustomInput from "../../shared/CustomInput";
import { Switch } from "antd";
import EditProductSuccess from "../../components/edit-product/EditProductSuccess";
import { useMutation } from "react-query";
import {
  editLoanProduct,
  getLoanProductById,
} from "../../sdk/products/products";
import { useError } from "../../context/ErrorProvider";
import Loading from "../../shared/Loading";
import useAuth from "../../hooks/useAuth";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";

const EditLoanProduct = () => {
  const { id } = useParams();
  const [productForm, setProductForm] = useState({
    // Product Details
    productType: "",
    productName: "",
    productDescription: "",

    // Product Terms
    interestRate: "",
    interestType: "",
    minimumLoanAmount: "",
    maximumLoanAmount: "",
    minimumDuration: "",
    maximumDuration: "",

    // Product Repayment Terms
    repaymentFrequency: "",
    gracePeriod: "",
    penaltyRate: "",

    // Eligibility & Conditions
    minimumAge: "",
    collateralRequired: false,

    // Fees
    processingFee: "",
    insuranceFee: "",
    otherCharges: "",

    // Terms & Conditions
    termsAndConditions: "",
    requiredDocuments: "",
  });
  const [openAddProductSuccess, setOpenAddProductSuccess] = useState(false);
  const { showError } = useError();
  const { auth } = useAuth();
  const navigate = useNavigate();

  const toggleAddProductSuccess = () => {
    setOpenAddProductSuccess(true);
  };
  const closeAddProductSuccess = () => {
    setOpenAddProductSuccess(false);
  };

  const handleCreateProduct = () => {
    mutate();
  };

  const { mutate, isLoading } = useMutation({
    mutationFn: () =>
      editLoanProduct(
        id,
        productForm.productName,
        productForm.productDescription,
        Number(productForm.interestRate),
        Number(productForm.minimumDuration),
        Number(productForm.maximumDuration),
        Number(productForm.maximumLoanAmount),
        Number(productForm.minimumLoanAmount),
        productForm.interestType,
        productForm.repaymentFrequency,
        Number(productForm.gracePeriod),
        auth?.user?.id,
        productForm?.processingFee,
        productForm?.insuranceFee,
        productForm.penaltyRate,
        productForm.requiredDocuments,
        productForm?.termsAndConditions,
        productForm?.otherCharges,
        productForm?.productType,
      ),
    onSuccess: () => {
      toggleAddProductSuccess();
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const { data: loanProduct } = useQuery({
    queryKey: [id],
    queryFn: () => getLoanProductById(id),
    initialData: {},
    onSuccess: (data) => {
      setProductForm({
        // Product Details
        productType: data?.loanType,
        productName: data?.name,
        productDescription: data?.description,

        // Product Terms
        interestRate: data?.defaultInterestRate,
        interestType: data?.repaymentType,
        minimumLoanAmount: data?.minLoanAmount,
        maximumLoanAmount: data?.maxLoanAmount,
        minimumDuration: data?.minLoanPeriod,
        maximumDuration: data?.maxLoanPeriod,

        // Product Repayment Terms
        repaymentFrequency: data?.repaymentFrequency || "",
        gracePeriod: data?.gracePeriod || "",
        penaltyRate: data?.penaltyFee || 0,

        // Eligibility & Conditions
        minimumAge: "",
        collateralRequired: false,

        // Fees
        processingFee: data?.processingFee || "",
        insuranceFee: data?.insuranceFee || "",
        otherCharges: data?.otherCharges || "",

        // Terms & Conditions
        termsAndConditions: data?.loanTermsAndConditions || "",
        requiredDocuments: data?.documentsRequired || "",
      });
    },
  });
  return (
    <>
      {openAddProductSuccess && (
        <EditProductSuccess closeEditProductSuccess={closeAddProductSuccess} />
      )}
      <div className="pl-[10px] w-full">
        <div className="flex items-center h-[40px] justify-between">
          <div className="flex items-center gap-[5px]">
            <ChevronLeft color="#000000" size={17} />
            <span
              onClick={() => navigate("/dashboard/loan-products")}
              className="cursor-pointer"
            >
              Loan Products
            </span>
            <ChevronRight color="#000000" size={17} />
            <span className="text-[16px] text-[#222222]">Edit a product</span>
          </div>
        </div>
        <p className="text-[20px] mb-[15px] text-[#333333] font-bold">
          Edit {loanProduct?.name}
        </p>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Product Details
          </p>
          <div className="flex items-start justify-between mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="product Type"
              >
                Product Type
              </label>
              <CustomSelect
                name="gender"
                value={productForm.productType}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    productType: e.target.value,
                  })
                }
                options={["Individual/Personal", "Salaried", "Development"]}
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="product name"
              >
                Product Name
              </label>
              <CustomInput
                placeholder="Enter your product name"
                type="text"
                value={productForm.productName}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    productName: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="product description"
              >
                Product Description
              </label>
              <CustomArea
                placeholder="Enter your product description"
                type="text"
                value={productForm.productDescription}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    productDescription: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Product Terms
          </p>
          <div className="flex items-start justify-between mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Interest Rate (%)"
              >
                Interest Rate (%)
              </label>
              <CustomInput
                placeholder="Enter interest rate"
                type="text"
                value={productForm.interestRate}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    interestRate: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="interest Type"
              >
                Interest Type
              </label>
              <CustomSelect
                name="interest type"
                value={productForm.interestType}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    interestType: e.target.value,
                  })
                }
                options={["standard", "reducing"]}
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="minimum Loan Amount (KES)"
              >
                Minimum Loan Amount (KES)
              </label>
              <CustomInput
                placeholder="Enter amount"
                type="text"
                value={productForm.minimumLoanAmount}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    minimumLoanAmount: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <div className="flex items-start justify-between my-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Maximum Loan Amount (KES)"
              >
                Maximum Loan Amount (KES)
              </label>
              <CustomInput
                placeholder="Enter amount"
                type="text"
                value={productForm.maximumLoanAmount}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    maximumLoanAmount: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="minimum Duration (Months)"
              >
                Minimum Duration (Months)
              </label>
              <CustomInput
                placeholder="Enter duration (months)"
                type="text"
                value={productForm.minimumDuration}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    minimumDuration: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="maximum Duration (Months)"
              >
                Maximum Duration (Months)
              </label>
              <CustomInput
                placeholder="Enter duration (months)"
                type="text"
                value={productForm.maximumDuration}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    maximumDuration: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Product Repayment Terms
          </p>
          <div className="flex items-start justify-between mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Repayment frequency"
              >
                Repayment Frequency
              </label>
              <CustomSelect
                name="Repayment frequency"
                value={productForm.repaymentFrequency}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    repaymentFrequency: e.target.value,
                  })
                }
                options={["weekly", "monthly"]}
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="grace Period (Months)"
              >
                Grace Period (Months)
              </label>
              <CustomSelect
                name="grace Period (Months)"
                value={productForm.gracePeriod}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    gracePeriod: e.target.value,
                  })
                }
                options={["1", "2"]}
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="penalty rate (% per late installment)"
              >
                Penalty Rate (% per late installment)
              </label>
              <CustomInput
                placeholder="Enter rate"
                type="text"
                value={productForm.penaltyRate}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    penaltyRate: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Eligibility & Conditions
          </p>
          <div className="flex items-start gap-[2%] mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Minimum age of borrower"
              >
                Minimum Age of Borrower
              </label>
              <CustomInput
                placeholder="Enter minimum age"
                type="text"
                value={productForm.minimumAge}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    minimumAge: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Collateral required"
              >
                Collateral Required
              </label>
              <div className="w-fit">
                <Switch
                  onChange={(value) => {
                    setProductForm({
                      ...productForm,
                      collateralRequired: value,
                    });
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">Fees</p>
          <div className="flex items-start justify-between mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Processing fee (%)"
              >
                Processing Fee (%)
              </label>
              <CustomInput
                placeholder="Enter rate"
                type="text"
                value={productForm.processingFee}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    processingFee: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="insuarance fee (%)"
              >
                Insuarance Fee (%)
              </label>
              <CustomInput
                placeholder="Enter rate (%)"
                type="text"
                value={productForm.insuranceFee}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    insuranceFee: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="other Charges"
              >
                Other Charges
              </label>
              <CustomInput
                placeholder="Enter charges (%)"
                type="text"
                value={productForm.otherCharges}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    otherCharges: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>
        <div>
          <p className="text-[#000000] text-[16px] font-semibold">
            Terms & Conditions
          </p>
          <div className="flex items-start gap-[2%] mb-[20px]">
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="Terms & Conditions"
              >
                Terms & Conditions
              </label>
              <CustomArea
                placeholder="Enter terms"
                type="text"
                value={productForm.termsAndConditions}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    termsAndConditions: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex w-[32%] flex-col">
              <label
                className="text-[#222222] font-medium text-[13px]"
                htmlFor="document required (List)"
              >
                Document Required (List)
              </label>
              <CustomArea
                placeholder="Enter documents"
                type="text"
                value={productForm.requiredDocuments}
                onChange={(e) =>
                  setProductForm({
                    ...productForm,
                    requiredDocuments: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end mb-[20px] gap-[10px]">
          <button
            disabled={isLoading}
            onClick={handleCreateProduct}
            className="bg-[#000000] h-[35px] rounded-[30px] text-[#ffffff] text-[14px] font-normal px-[20px] flex items-center justify-center gap-[5px]"
          >
            {isLoading ? <Loading /> : <Plus size={16} color="#ffffff" />}
            Edit product
          </button>
        </div>
      </div>
    </>
  );
};

export default EditLoanProduct;
